import { useTheme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import React from 'react';
import { connect } from 'react-redux';
import { Link as RouterLink, matchPath, useLocation } from 'react-router-dom';

import { CopilotDrawer, CopilotDrawerTrigger, useCopilotDrawerState } from 'components/Copilot';
import { NUCLEAI_ORIGINS_ROUTE_PATH } from 'components/Pages/NucleaiOrigins/navigation';
import ControlPanel from 'components/SiteHeader/ControlPanel';
import LabDropdown from 'components/SiteHeader/LabDropdown';
import NucleaiOriginsButton from 'components/SiteHeader/NucleaiOriginsButton';
import { getSiteHeaderResponsiveLayout } from 'components/SiteHeader/layout';
import { getLocalDevelopmentEnvironmentLabel } from 'utils/deploymentEnvironment';
import { useColorMode } from '../ColorModeContext';

import './SiteHeader.scss';

interface Props {
  isLoggedIn: boolean;
}

export const SITE_HEADER_NUMBER = 62;
export const SITE_HEADER_HEIGHT = `${SITE_HEADER_NUMBER}px`;

const getViewportWidth = () => {
  if (typeof window === 'undefined') {
    return 1280;
  }

  return window.innerWidth;
};

export const SiteHeaderBase: React.FunctionComponent<React.PropsWithChildren<Props>> = ({ isLoggedIn }) => {
  const colorMode = useColorMode();
  const { mode } = colorMode;
  const theme = useTheme();
  const localDevelopmentEnvironmentLabel = getLocalDevelopmentEnvironmentLabel();
  const location = useLocation();
  const { copilotDrawerOpen, copilotDrawerWidth } = useCopilotDrawerState();
  const [viewportWidth, setViewportWidth] = React.useState(() => getViewportWidth());
  const copilotDrawerLayoutTransitionDuration = `var(--copilot-drawer-layout-transition-duration, ${theme.transitions.duration.enteringScreen}ms)`;
  const shouldAnimateCopilotTrigger = ![
    '/procedures/:id',
    '/slides/:slideId',
    '/study/:studyId/procedures/:id',
    '/study/:studyId/slides/:slideId',
  ].some((pattern) => Boolean(matchPath(pattern, location.pathname)));
  const shouldHideLabDropdown = location.pathname === NUCLEAI_ORIGINS_ROUTE_PATH;
  const availableHeaderWidth = Math.max(0, viewportWidth - (copilotDrawerOpen ? copilotDrawerWidth : 0));
  const headerLayout = React.useMemo(
    () => getSiteHeaderResponsiveLayout(availableHeaderWidth, { preferCompactControls: copilotDrawerOpen }),
    [availableHeaderWidth, copilotDrawerOpen]
  );

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setViewportWidth(getViewportWidth());
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <AppBar
        sx={{
          backgroundColor: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.grey[900],
          color: theme.palette.mode === 'light' ? theme.palette.grey[900] : theme.palette.grey[50],
          boxShadow: 'none',
          borderBottom: `1px solid ${
            theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800]
          }`,
          minHeight: SITE_HEADER_HEIGHT,
          display: 'flex',
          justifyContent: 'center',
          left: 0,
          right: 'auto',
          width: copilotDrawerOpen ? `calc(100% - ${copilotDrawerWidth}px)` : '100%',
          transition: `width ${copilotDrawerLayoutTransitionDuration} ${theme.transitions.easing.easeOut}`,
        }}
      >
        <Grid
          container
          alignSelf="center"
          alignItems="center"
          wrap="nowrap"
          sx={{ px: availableHeaderWidth < 840 ? 2 : 3, minWidth: 0 }}
        >
          <Grid
            item
            container
            alignItems="center"
            wrap="nowrap"
            columnGap={headerLayout.leftClusterGap}
            sx={{ width: 'auto', minWidth: 0, flex: '0 1 auto' }}
          >
            <Grid item>
              <Link
                component={RouterLink}
                to={{
                  pathname: '/',
                }}
                className="logo"
              >
                <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  <img
                    src={`static/logo_${mode == 'dark' ? 'white' : 'black'}.svg`}
                    alt="nucleai logo"
                    className="icon-logo"
                  />
                  {localDevelopmentEnvironmentLabel ? (
                    <Box
                      component="span"
                      data-testid="deployment-environment-badge"
                      sx={{
                        position: 'absolute',
                        right: -10,
                        bottom: -8,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 999,
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        lineHeight: 1,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        backgroundColor:
                          theme.palette.mode === 'light' ? theme.palette.warning.main : theme.palette.warning.dark,
                        color: theme.palette.common.white,
                        border: `1px solid ${
                          theme.palette.mode === 'light' ? theme.palette.warning.dark : theme.palette.warning.light
                        }`,
                        boxShadow: theme.shadows[1],
                      }}
                    >
                      {localDevelopmentEnvironmentLabel}
                    </Box>
                  ) : null}
                </Box>
              </Link>
            </Grid>
            {isLoggedIn && (
              <>
                <Grid item>
                  <Box
                    aria-hidden
                    sx={{
                      width: '1px',
                      height: 32,
                      backgroundColor:
                        theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
                    }}
                  />
                </Grid>
                <Grid item sx={{ minWidth: 0 }}>
                  {!shouldHideLabDropdown && <LabDropdown responsiveWidth={headerLayout.labSelectWidth} />}
                </Grid>
                <Grid item>
                  <NucleaiOriginsButton iconOnly={headerLayout.originsIconOnly} />
                </Grid>
              </>
            )}
          </Grid>
          <Grid
            item
            container
            alignItems="center"
            justifyContent="end"
            wrap="nowrap"
            columnGap={headerLayout.rightClusterGap}
            sx={{ width: 'auto', ml: 'auto', minWidth: 0, flex: '1 1 auto', pl: headerLayout.clusterBoundaryGap }}
          >
            {isLoggedIn && (
              <ControlPanel
                controlGap={headerLayout.controlPanelGap}
                resultsModeIconOnly={headerLayout.resultsModeIconOnly}
                userMenuIconOnly={headerLayout.userMenuIconOnly}
                userMenuMaxWidth={headerLayout.userMenuMaxWidth}
              />
            )}
            {isLoggedIn && (
              <Grid item sx={{ minWidth: 0 }}>
                <CopilotDrawerTrigger
                  animateOnEnter={shouldAnimateCopilotTrigger}
                  iconOnly={headerLayout.copilotTriggerIconOnly}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </AppBar>
      <Box
        sx={{
          position: 'fixed',
          top: SITE_HEADER_HEIGHT,
          left: 0,
          right: copilotDrawerOpen ? `${copilotDrawerWidth}px` : 0,
          height: 10,
          pointerEvents: 'none',
          background:
            theme.palette.mode === 'light'
              ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.06), rgba(15, 23, 42, 0))'
              : 'linear-gradient(180deg, rgba(15, 23, 42, 0.22), rgba(15, 23, 42, 0))',
          transition: `right ${copilotDrawerLayoutTransitionDuration} ${theme.transitions.easing.easeOut}`,
        }}
      />
      {isLoggedIn && <CopilotDrawer />}
    </>
  );
};

const mapStateToProps = (state: any) => ({
  isLoggedIn: Boolean(state.auth.accessToken),
});

export default connect(mapStateToProps)(SiteHeaderBase);
